import { clearAppSnapshot } from "../services/appAccessSnapshot";
import { SuiteProductAccess } from '@/components/suite/SuiteProductAccess';
import { redirectToTrialBilling } from '../helpers/trialPayment';
import { beginSuiteSignIn, suiteSignInState } from '@/helpers/suiteAuthFlow';
import { useResultToast } from '@/context/ResultToastContext';
import { AdminPageHero } from '@/components/common/AdminPageHero';
import { AuthStatus } from '@/components/auth/AuthStatus';
import { inviteUser, loadUsers, reassignUserWork } from "@/services/userService";
import type { CrmUser } from "@/models/api";
import { useEffect, useState, useRef, type ReactNode } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Alert, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import {
  accountsPortal,
  accountsRequest,
  centralManager,
  crmRole,
  centralSession,
  type DirectoryContext,
  type Workspace,
} from "@/services/centralAuth";
import { crmReturnPath } from "@/helpers/authNavigation";
import { readSession, writeSession } from "@/helpers/storage";
import { publishSession } from "@/services/apiClient";
import { useAuth } from "@/context/AuthContext";
export function CentralSignIn() {
  const { session, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(() => suiteSignInState('crm').error);
  const started = useRef(false);
  const [signedOut, setSignedOut] = useState(() => suiteSignInState('crm').signedOut);
  const returnTo = crmReturnPath(location.state?.from);
  const connect = () => {
    setSignedOut(false);
    return beginSuiteSignIn('crm', options => centralManager.signinRedirect(options), returnTo).catch(e => setError(e.message));
  };
  useEffect(() => {
    if (!ready || started.current) return;
    started.current = true;
    if (session) {
      localStorage.removeItem('crm-explicit-signout');
      navigate(session.requiresBusinessSelection ? '/select-business' : returnTo, { replace: true, state: { returnTo } });
    }
    else {
      const interrupted = suiteSignInState('crm').error;
      if (interrupted) setError(interrupted);
      else if (!signedOut && !error) void connect();
    }
  }, [ready, session, navigate, returnTo, signedOut]);
  return <AuthStatus product="CRM" loading={!error && !signedOut} title={signedOut ? "You're signed out" : error ? 'Sign-in interrupted' : undefined}
    description={signedOut ? 'Sign in again when you are ready to use CRM.' : undefined}>
    {(error || signedOut) && <>{error && <Alert variant="danger">{error}</Alert>}<Button onClick={() => { setError(''); void connect(); }}>{signedOut ? 'Sign in to CRM' : 'Try signing in again'}</Button></>}
  </AuthStatus>;
}

let callback:
  ReturnType<typeof centralManager.signinRedirectCallback> | undefined;
export function CentralCallback() {
  const [error, setError] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    callback ??= centralManager.signinRedirectCallback().then(user => { clearAppSnapshot(); return user; });
    callback
      .then(async user => { return ({ session: await centralSession(localStorage.getItem('crm-last-membership')), returnTo: crmReturnPath((user.state as { returnTo?: string } | undefined)?.returnTo) }); })
      .then(({ session, returnTo }) => {
        writeSession(session);
        publishSession(session);
        if (active)
          navigate(
            session.requiresBusinessSelection ? "/select-business" : returnTo,
            { replace: true, state: { returnTo } },
          );
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [navigate]);
  return (
    <AuthStatus
      product="CRM"
      loading={!error}
      title={error ? 'Sign-in interrupted' : undefined}
      description={error ? 'Please try signing in again to continue.' : undefined}
    >
      {error && <>
        <Alert variant="danger">{error}</Alert>
        <Link className="btn btn-primary" to="/signin" reloadDocument>Try signing in again</Link>
      </>}
    </AuthStatus>
  );
}
export function AccountsRedirect({ path }: { path: string }) {
  const { session } = useAuth();
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    const redirect = async () => {
      let destination = path;
      if (path === '/subscriptions') {
        const data = await accountsRequest<DirectoryContext>('/crm/context');
        const workspace = data.workspaces.find(value => value.businessId === session?.businessId);
        if (!workspace?.canManageBilling) throw new Error('Billing access is not available for this business. Contact its billing owner.');
        destination = `/subscriptions/${workspace.resourceId}?accountId=${encodeURIComponent(workspace.accountId)}`;
      }
      if (active) window.location.replace(`${accountsPortal}${destination}`);
    };
    void redirect().catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [path, session?.businessId]);
  return <AuthStatus loading={!error} description={error || "Taking you to your SmoothBadger account."} />;
}
export function CentralAccess({ children }: { children?: ReactNode }) {
  const { session } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace>();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const data = await accountsRequest<DirectoryContext>("/crm/context");
        if (active) {
          const selected = data.workspaces.find(x => x.businessId === session?.businessId)
          if (redirectToTrialBilling(selected, accountsPortal)) return;
          const current = readSession()
          if (selected && current && current.businessId === selected.businessId && current.role !== crmRole(selected.role)) {
            const next = { ...current, role: crmRole(selected.role) }
            writeSession(next); publishSession(next)
          }
          setWorkspace(
            data.workspaces.find((x) => x.businessId === session?.businessId),
          );
          setLoaded(true);
          setError("");
        }
      } catch (e) {
        if (active)
          setError(e instanceof Error ? e.message : "Unable to check access.");
      }
    };
    void load();
    const timer = setInterval(() => void load(), 30000);
    window.addEventListener("crm-access-changed", load);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("crm-access-changed", load);
    };
  }, [session?.businessId]);
  if (error && !loaded)
    return (
      <Alert variant="danger">
        {error} <a href={accountsPortal}>Open Accounts</a>
      </Alert>
    );
  if (!loaded) return null;
  if (!workspace) return <SuiteProductAccess product="crm" selectionHref="/select-business" />;
  if (!workspace.entitlement.hasAccess)
    return (
      <Card body>
        <h1>CRM access is locked</h1>
        <p>
          {workspace?.canManageBilling
            ? "Choose and pay for a subscription in Accounts to continue, or review any account restrictions."
            : "Select another business or contact your billing owner."}
        </p>
        {workspace?.canManageBilling && (
          <a href={`${accountsPortal}/subscriptions/${workspace.resourceId}?accountId=${encodeURIComponent(workspace.accountId)}`}>
            Manage subscription
          </a>
        )}
        <Link to="/select-business">Select a business</Link>
        <a href={`${accountsPortal}/crm`}>Review CRM access in Accounts</a>
      </Card>
    );
  const access = workspace.entitlement;
  const grace = ["TrialGrace", "PaymentGrace"].includes(access.reason);
  return (
    <>
      {error && <Alert variant="warning" role="status">Unable to verify access right now. Your open work has been kept.</Alert>}
      {(grace || access.showTrialWarning || access.showExpiryWarning) && (
        <Alert variant={grace ? "danger" : "warning"}>
          {grace
            ? "Your grace period ends on "
            : access.showTrialWarning
              ? "Your trial ends on "
              : "Your current access period ends on "}
          {new Date(
            (access.showTrialWarning && !grace
              ? access.trialEndsAt
              : access.accessUntil) || "",
          ).toLocaleDateString()}
          .{" "}
          {access.showTrialWarning && "Choose and pay for a subscription to continue after your trial. You will not be charged automatically. "}
          {workspace.canManageBilling ? (
            <a href={`${accountsPortal}/subscriptions/${workspace.resourceId}?accountId=${encodeURIComponent(workspace.accountId)}`}>
              Manage subscription
            </a>
          ) : (
            "Contact your billing owner."
          )}
        </Alert>
      )}
      {children}
    </>
  );
}
type Member = {
  id: string;
  localMembershipId?: string;
  localUserId?: string;
  name: string;
  email: string;
  role: number;
  enabled: boolean;
  pending: boolean;
  bookingColour: string;
  rowVersion: string;
};
export function CentralUsers() {
  const [workspace, setWorkspace] = useState<Workspace>();
  const [members, setMembers] = useState<Member[]>([]);
  const [localUsers, setLocalUsers] = useState<CrmUser[]>([]);
  const [reassigning, setReassigning] = useState<CrmUser | null>(null);
  const [inviting, setInviting] = useState(false);
  const [toUser, setToUser] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState(3);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [busy, setBusy] = useState(false);
  const notify = useResultToast();
  const [pendingChange, setPendingChange] = useState<{ id: string; label: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const canManage = !!workspace && workspace.role <= 2;
  const destinations = localUsers.filter(
    (user) => user.enabled && user.role !== "ReadOnly" && user.crmAccountUserId !== reassigning?.crmAccountUserId,
  );

  const load = async () => {
    const context = await accountsRequest<DirectoryContext>("/crm/context");
    const selected = readSession()?.businessId;
    const w = context.workspaces.find((item) => item.businessId === selected);
    setWorkspace(w);
    if (w) {
      setMembers(await accountsRequest(`/crm/resources/${w.resourceId}/members`));
      setLocalUsers(await loadUsers());
    }
  };
  const run = async (work: () => Promise<void>, dialog = false) => {
    setBusy(true);
    const reportError = dialog ? setDialogError : setError;
    reportError("");
    try {
      await work();
    } catch (e) {
      reportError(e instanceof Error ? e.message : "Unable to save.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { void run(load).finally(() => setLoading(false)); }, []);

  const save = async (member: Member, remove = false) => {
    const previous = members.find((item) => item.id === member.id);
    const statusChanged = previous?.enabled !== member.enabled;
    const operation = remove ? "Removing" : statusChanged ? member.enabled ? "Enabling" : "Disabling" : "Updating permissions for";
    setPendingChange({ id: member.id, label: `${operation} ${member.name}…` });
    try {
      await run(async () => {
        await accountsRequest(
          `/crm/resources/${workspace!.resourceId}/members/${member.id}`,
          "PUT",
          { ...member, remove },
        );
        notify(true, remove ? `${member.name} removed from this business.` : statusChanged
          ? `${member.name} ${member.enabled ? "enabled" : "disabled"}.`
          : `Permissions updated for ${member.name}.`);
        await load();
      });
    } finally {
      setPendingChange(null);
    }
  };

  function localUser(member: Member) {
    return localUsers.find((user) =>
      user.crmAccountUserId === member.localMembershipId ||
      user.userId === member.localUserId ||
      user.emailAddress?.toLowerCase() === member.email.toLowerCase(),
    );
  }

  function openInvite() {
    setEmail("");
    setName("");
    setRole(3);
    setDialogError("");
    setInviting(true);
  }

  return (
    <>
      <AdminPageHero
        title="Business users"
        summary="Manage access to this business. Personal details and passwords are managed in Accounts."
      >
        <Link className="btn btn-outline-primary" to="/admin">All settings</Link>
        {canManage && <Button disabled={busy} onClick={openInvite}>Invite user</Button>}
      </AdminPageHero>
      {error && <Alert variant="danger">{error}</Alert>}
      {(loading || pendingChange) && <div className="d-flex align-items-center gap-2 mb-3" role="status" aria-live="polite">
        <Spinner animation="border" size="sm" aria-hidden="true" />
        <span>{pendingChange?.label ?? "Loading users…"}</span>
      </div>}
      <section className="admin-panel" aria-busy={!!pendingChange || loading}>
        <Table responsive hover className="align-middle mb-0">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Permission</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {members.map((member) => {
              const source = localUser(member);
              return (
                <tr key={member.id}>
                  <td>{member.name}</td>
                  <td>{member.email}</td>
                  <td>
                    <Form.Select
                      aria-label={`Permission for ${member.name}`}
                      disabled={busy || (workspace?.role !== 1 && member.role === 1)}
                      value={member.role}
                      onChange={(event) => void save({ ...member, role: +event.target.value })}
                    >
                      {["Owner", "Administrator", "User", "Read only"].map((label, index) => (
                        <option key={label} value={index + 1}>{label}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>{member.pending ? "Invitation pending" : member.enabled ? "Active" : "Disabled"}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-2">
                      {canManage && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          aria-label={`Reassign work for ${member.name}`}
                          disabled={busy || member.pending || !source}
                          onClick={() => {
                            if (!source) return;
                            setReassigning(source);
                            setToUser("");
                            setDialogError("");
                                                  }}
                        >Reassign work</Button>
                      )}
                      <Button
                        size="sm"
                        disabled={busy || member.pending}
                        onClick={() => void save({ ...member, enabled: !member.enabled })}
                      >{member.enabled ? "Disable" : "Enable"}</Button>
                      {workspace?.role === 1 && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={busy}
                          onClick={() => {
                            if (window.confirm(`Remove ${member.name} from this business? Access to other businesses will be retained.`))
                              void save(member, true);
                          }}
                        >Remove</Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <p className="small mt-3 mb-0"><a href={`${accountsPortal}/profile`}>Manage your personal details in Accounts</a></p>
      </section>

      <Modal
        show={!!reassigning}
        onHide={() => { if (!busy) setReassigning(null); }}
        backdrop={busy ? "static" : true}
        keyboard={!busy}
        aria-labelledby="reassign-work-title"
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title id="reassign-work-title">Reassign work</Modal.Title>
        </Modal.Header>
        <Form onSubmit={(event) => {
          event.preventDefault();
          if (!reassigning || !toUser) return;
          void run(async () => {
            await reassignUserWork(reassigning.crmAccountUserId, toUser);
            setReassigning(null);
            setToUser("");
            notify(true, "Customers and open actions reassigned.");
          }, true);
        }}>
          <Modal.Body>
            {dialogError && <Alert variant="danger">{dialogError}</Alert>}
            <p>Move this person's customers and open actions to another active user.</p>
            <Form.Group className="mb-3" controlId="reassign-from">
              <Form.Label>From user</Form.Label>
              <Form.Control readOnly value={reassigning?.fullName || reassigning?.emailAddress || ""} />
            </Form.Group>
            <Form.Group controlId="reassign-to">
              <Form.Label>To user</Form.Label>
              <Form.Select required autoFocus disabled={busy} value={toUser} onChange={(event) => setToUser(event.target.value)}>
                <option value="">Select an active user</option>
                {destinations.map((user) => (
                  <option key={user.crmAccountUserId} value={user.crmAccountUserId}>{user.fullName || user.emailAddress}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {!destinations.length && <Alert variant="info" className="mt-3 mb-0">There are no other active users who can receive this work.</Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" disabled={busy} onClick={() => setReassigning(null)}>Cancel</Button>
            <Button type="submit" disabled={busy || !toUser}>{busy ? "Reassigning…" : "Reassign work"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={inviting}
        onHide={() => { if (!busy) setInviting(false); }}
        backdrop={busy ? "static" : true}
        keyboard={!busy}
        aria-labelledby="invite-user-title"
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title id="invite-user-title">Invite user</Modal.Title>
        </Modal.Header>
        <Form onSubmit={(event) => {
          event.preventDefault();
          if (!workspace) return;
          void run(async () => {
            await inviteUser({ email, name, role });
            setInviting(false);
            notify(true, "Invitation queued for delivery.");
            try { await load(); }
            catch (e) { setError(e instanceof Error ? e.message : "Unable to refresh users."); }
          }, true);
        }}>
          <Modal.Body>
            {dialogError && <Alert variant="danger">{dialogError}</Alert>}
            <Form.Group className="mb-3" controlId="invite-user-name">
              <Form.Label>Name</Form.Label>
              <Form.Control required autoFocus disabled={busy} maxLength={200} value={name} onChange={(event) => setName(event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="invite-user-email">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" required disabled={busy} maxLength={200} value={email} onChange={(event) => setEmail(event.target.value)} />
            </Form.Group>
            <Form.Group controlId="invite-user-role">
              <Form.Label>Permission</Form.Label>
              <Form.Select disabled={busy} value={role} onChange={(event) => setRole(+event.target.value)}>
                {["Owner", "Administrator", "User", "Read only"].map((label, index) => (
                  <option key={label} value={index + 1} disabled={index === 0 && workspace?.role !== 1}>{label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" disabled={busy} onClick={() => setInviting(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? "Sending…" : "Send invitation"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
